
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link2Icon, CheckIcon, UnlinkIcon, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/hooks/use-toast';
import { useBroker } from '@/hooks/use-broker';

export const BrokerLinkDialog = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { 
    linkedBroker, 
    referralCode, 
    setReferralCode, 
    linkToBroker,
    unlinkBroker,
    isLinking,
    isUnlinking,
    isLoading: isBrokerLoading
  } = useBroker();
  
  const { toast } = useToast();
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleLinkToBroker = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!referralCode.trim()) return;
    
    setHasError(false);
    setErrorMessage('');
    
    const result = await linkToBroker(referralCode.trim());
    if (!result.success) {
      setHasError(true);
      setErrorMessage(result.message);
      toast({
        title: 'Unable to link to partner',
        description: result.message,
        variant: 'destructive'
      });
    } else {
      setIsOpen(false);
      setReferralCode('');
    }
  };

  const handleUnlinkBroker = async () => {
    if (window.confirm('Are you sure you want to unlink from your partner? This action cannot be undone.')) {
      const success = await unlinkBroker();
      if (success) {
        setIsOpen(false);
        toast({
          title: 'Successfully unlinked',
          description: 'You have been unlinked from your partner'
        });
      }
    }
  };

  if (isBrokerLoading) {
    return (
      <Button variant="ghost" size="sm" disabled>
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Loading...
      </Button>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {linkedBroker ? (
            <>
              <CheckIcon className="h-4 w-4 text-primary" />
              Partner Connected
            </>
          ) : (
            <>
              <Link2Icon className="h-4 w-4" />
              Link Account to a Partner
            </>
          )}
        </Button>
      </DialogTrigger>
      
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {linkedBroker ? (
              <>
                <CheckIcon className="h-5 w-5 text-primary" />
                Partner Connected
              </>
            ) : (
              <>
                <Link2Icon className="h-5 w-5" />
                Link to a Partner
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {linkedBroker ? (
            <div className="space-y-4">
              <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                <p className="text-sm">
                  You're connected to{" "}
                  <span className="font-medium">
                    {linkedBroker.broker_name || "your partner"}
                  </span>
                  {" "}who can assist with your PEO applications.
                </p>
                {linkedBroker.description && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {linkedBroker.description}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end">
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={handleUnlinkBroker}
                  disabled={isUnlinking}
                >
                  {isUnlinking ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <UnlinkIcon className="h-4 w-4 mr-2" />
                  )}
                  {isUnlinking ? "Unlinking..." : "Unlink Partner"}
                </Button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleLinkToBroker} className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Enter your partner's account linking code to grant them access to your account. Your partner can view, manage, and complete your PEO applications and quotes as another trusted resource.
                </p>
                
                {hasError && (
                  <Alert className="mb-4">
                    <AlertDescription>
                      {errorMessage || "Make sure you've entered the exact account linking code provided by your partner."}
                    </AlertDescription>
                  </Alert>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="referral-code">Partner Account Linking Code</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="referral-code"
                      placeholder="Enter account linking code (e.g., REF-12345678)"
                      value={referralCode}
                      onChange={(e) => setReferralCode(e.target.value)}
                      disabled={isLinking}
                    />
                    <Button 
                      type="submit" 
                      disabled={isLinking || !referralCode.trim()}
                    >
                      {isLinking ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Linking...
                        </>
                      ) : (
                        <>
                          <Link2Icon className="h-4 w-4 mr-2" />
                          Link
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
